import {
  CheckCircleOutlined,
  CloseOutlined,
  ManageHistory,
  NotInterestedOutlined,
  ReportProblemOutlined,
} from '@mui/icons-material';
import { Container, IconButton, List, styled, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useIndexRun } from '../../../hooks/useIndexRun';
import { selectFolders } from '../../../redux/reducers/folderReducer';
import { StyledLoadingButton, StyledTab, StyledTabs } from '../../../theme/CustomComponents';
import type {
  CategoryProgress,
  ProjectRun,
  ProjectWithLastScanned,
  RunProgress,
} from '../../../Types/dealGPT';
import {
  DealGPTDiscoveryView,
  DealGPTQuestionStatus,
  ProjectRunStatus,
} from '../../../Types/enums';
import DebouncedSearchInput from '../../DebouncedSearchInput';
import LoadingMask from '../../LoadingMask';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { KeyQuestionsSummary } from '../PhaseTwo/KeyQuestionSummary/KeyQuestionSummary';
import ProjectRunsDialog from '../PhaseTwo/ProjectRunsDialog';
import QuestionPreferencesDialog from '../PhaseTwo/QuestionPreferencesDialog';
import { ScopeAccordion } from '../ScopeAccordion';
import StatusProgressBar from '../StatusProgressBar';

const ButtonContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
});

const ProjectRunSelectorContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '16px',
  marginTop: '8px',

  [theme.breakpoints.up('sm')]: {
    padding: '0px',
  },
}));

const FilterContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '16px',
  marginTop: '8px',

  [theme.breakpoints.up('sm')]: {
    padding: '0px',
  },
}));

type ScopeFilterProps = {
  selected?: boolean;
};

const ScopeFilter = styled(Typography)<ScopeFilterProps>(({ selected = false }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '125%',
  color: '#092B49',
  backgroundColor: selected ? '#CED7E6' : '#E8EEF8',
  borderRadius: '100px',
  padding: '8px 16px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}));

const ScopesContainer = styled(List)({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '50px',
});

export type DiscoveryViewProps = {
  project: ProjectWithLastScanned;
  runScopes: string[];
  statuses: DealGPTQuestionStatus[];
  selectedStatusFilters: DealGPTQuestionStatus[];
  searchQuery: string;
  isLoadingRunProgress: boolean;
  isLoadingReport: boolean;
  openQuestionPreferences: boolean;
  lastScanned: string;
  setShouldFetchRunProgress: (isRescan: boolean) => void;
  expandAllCategories: boolean;
  projectRuns: ProjectRun[] | undefined;
  selectedRun: ProjectRun | undefined;
  runProgressData: CategoryProgress[];
  aggregateProgressData: RunProgress;
  handleQuestionsPreferencesChanged: (newRun: boolean, collections: string[]) => void;
  handleOpenQuestionPreferences: () => void;
  handleCloseQuestionPreferences: () => void;
  handleStartRun: () => void;
  handleGenerateReport: () => void;
  setSelectedRun: (run: ProjectRun) => void;
  isScopeSelected: (scope: string) => boolean;
  isStatusSelected: (status: DealGPTQuestionStatus) => boolean;
  toggleScopeFilters: (scope: string) => void;
  toggleStatusFilters: (status: DealGPTQuestionStatus) => void;
  handleClearScopeFilters: () => void;
  handleClearStatusFilters: () => void;
  toggleCollapseExpandAllCategories: () => void;
  setSearchQuery: (query: string) => void;
  discoveryView: DealGPTDiscoveryView;
  setDiscoveryView: (view: DealGPTDiscoveryView) => void;
};

const DiscoveryView = ({
  project,
  runScopes,
  statuses,
  selectedStatusFilters,
  searchQuery,
  isLoadingRunProgress,
  isLoadingReport,
  openQuestionPreferences,
  lastScanned,
  setShouldFetchRunProgress,
  expandAllCategories,
  projectRuns,
  selectedRun,
  runProgressData,
  aggregateProgressData,
  handleQuestionsPreferencesChanged,
  handleOpenQuestionPreferences,
  handleCloseQuestionPreferences,
  handleStartRun,
  handleGenerateReport,
  setSelectedRun,
  isScopeSelected,
  isStatusSelected,
  toggleScopeFilters,
  toggleStatusFilters,
  handleClearScopeFilters,
  handleClearStatusFilters,
  toggleCollapseExpandAllCategories,
  setSearchQuery,
  discoveryView,
  setDiscoveryView,
}: DiscoveryViewProps): JSX.Element => {
  const { currentFolder } = useSelector(selectFolders);
  const [hasRun, setHasRun] = useState<boolean>(true);
  const [discoverySearchSelected, setDiscoverySearchSelected] = useState<boolean>(false);
  const analytics = useAnalytics();
  const [selectedRunIndex, setSelectedRunIndex] = useState<number>(
    projectRuns
      ? projectRuns.length - projectRuns.map((run) => run.id).indexOf(selectedRun?.id ?? '')
      : -1
  );
  const [openProjectRunsDialog, setOpenProjectRunsDialog] = useState<boolean>(false);
  const { isIndexing } = useIndexRun(currentFolder?.indexIds?.[0]);

  const scopesProgress = useMemo(
    () =>
      runProgressData.reduce((acc, categoryProgress) => {
        (acc[categoryProgress.scope] ??= []).push(categoryProgress);
        return acc;
      }, {} as Record<string, CategoryProgress[]>),
    [runProgressData]
  );

  useEffect(() => {
    const handleSetDefaultRun = () => {
      if (projectRuns) {
        if (!selectedRun && projectRuns.length != 0) {
          const runToSet = projectRuns[0];
          setSelectedRun(runToSet);
        }
      }
    };

    handleSetDefaultRun();
  });

  useEffect(() => {
    if (projectRuns) {
      if (projectRuns.length === 0) {
        setHasRun(false);
      } else {
        setHasRun(true);
      }
    }
  }, [project.lastDiscoveryRunId, projectRuns]);

  useEffect(() => {
    setSelectedRunIndex(
      projectRuns
        ? projectRuns.length - projectRuns.map((run) => run.id).indexOf(selectedRun?.id ?? '')
        : -1
    );
  }, [selectedRun, projectRuns]);

  const formatDateTime = (isoString: string): string => {
    const date = new Date(isoString);

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };

    return date.toLocaleString('en-US', options);
  };

  return (
    <div>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <ButtonContainer>
              <StyledLoadingButton
                variant="outlined"
                loadingPosition="end"
                endIcon={<></>}
                onClick={() => {
                  analytics.event(
                    AnalyticsEvent.WM_IA_D_RESCAN_DOCUMENTS,
                    {
                      id: project.id,
                    },
                    {
                      projectId: project.id,
                    }
                  );
                  handleOpenQuestionPreferences();
                }}
                loading={project.projectRunStatus === ProjectRunStatus.PROCESSING_QUESTIONS}
                disabled={isIndexing}
              >
                Import Questions
              </StyledLoadingButton>
              <StyledLoadingButton
                variant="contained"
                loadingPosition="end"
                endIcon={<></>}
                onClick={handleStartRun}
                loading={project.projectRunStatus === ProjectRunStatus.PROCESSING_QUESTIONS}
                disabled={isIndexing}
              >
                Process Questions
              </StyledLoadingButton>
            </ButtonContainer>
            {openQuestionPreferences && (
              <QuestionPreferencesDialog
                runScopes={runScopes ?? []}
                handleQuestionsPreferencesChanged={handleQuestionsPreferencesChanged}
                onClose={handleCloseQuestionPreferences}
              />
            )}
            <Typography
              sx={{ fontSize: '14px', fontWeight: '300', fontStyle: 'italic', marginTop: '8px' }}
            >
              Last Scanned: {lastScanned}
            </Typography>
            {hasRun && (
              <Typography
                sx={{ fontSize: '14px', fontWeight: '300', fontStyle: 'italic', marginTop: '8px' }}
              >
                Status: {project.projectRunStatus}
              </Typography>
            )}
          </div>
          <div id="project-runs">
            {projectRuns && selectedRun && !isLoadingRunProgress && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: '400', color: '#757575' }}>
                    Project Run
                  </Typography>
                  <ManageHistory
                    onClick={() => {
                      analytics.event(
                        AnalyticsEvent.WM_IA_D_OPEN_RUN_DIALOG,
                        {
                          id: project.id,
                        },
                        {
                          projectId: project.id,
                        }
                      );
                      setOpenProjectRunsDialog(true);
                    }}
                    sx={{ color: '#092B49', cursor: 'pointer' }}
                  />
                </div>
                {projectRuns[projectRuns.length - selectedRunIndex] && (
                  <ProjectRunSelectorContainer>
                    <div
                      style={{
                        border: '1px solid black',
                        padding: '10px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        width: '250px',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        analytics.event(
                          AnalyticsEvent.WM_IA_D_OPEN_RUN_DIALOG,
                          {
                            id: project.id,
                          },
                          {
                            projectId: project.id,
                          }
                        );
                        setOpenProjectRunsDialog(true);
                      }}
                    >
                      <Typography variant="subtitle2">
                        Run {selectedRunIndex}:{' '}
                        {projectRuns[projectRuns.length - selectedRunIndex].endedAt
                          ? 'Complete'
                          : 'Incomplete'}
                      </Typography>
                      <Typography variant="body1" fontStyle="italic">
                        Date: {formatDateTime(selectedRun.createdAt ?? '')}
                      </Typography>
                    </div>
                  </ProjectRunSelectorContainer>
                )}
                <Typography
                  variant="body1"
                  sx={{ display: 'flex', justifyContent: 'flex-end', fontStyle: 'italic' }}
                >
                  run {selectedRunIndex} of {projectRuns.length}
                </Typography>
                <ProjectRunsDialog
                  selectedRun={selectedRun}
                  projectRuns={projectRuns}
                  openDialog={openProjectRunsDialog}
                  handleCloseDialog={() => setOpenProjectRunsDialog(false)}
                  selectRun={setSelectedRun}
                />
              </>
            )}
          </div>
        </div>
        {hasRun && (
          <div>
            <div id="cover-while-loading" style={{ position: 'relative', minHeight: '100%' }}>
              {isLoadingRunProgress && <LoadingMask isLoading={true} />}
              <div id="scopes-and-categories" style={{ marginTop: '16px' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: '400', color: '#757575' }}>
                  Scope area:
                </Typography>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <FilterContainer maxWidth="lg" sx={{ marginBottom: '24px' }}>
                    {runScopes.map((scope) => (
                      <ScopeFilter
                        key={scope}
                        selected={isScopeSelected(scope)}
                        onClick={() => toggleScopeFilters(scope)}
                      >
                        {scope}
                        {isScopeSelected(scope) && <CloseOutlined sx={{ fontSize: '16px' }} />}
                      </ScopeFilter>
                    ))}
                    <Typography
                      sx={{
                        fontSize: '14px',
                        alignSelf: 'center',
                        color: '#00a3ff',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        handleClearScopeFilters();
                        handleClearStatusFilters();
                      }}
                    >
                      Clear Filters
                    </Typography>
                  </FilterContainer>
                </div>
              </div>
              <StyledTabs
                value={discoveryView}
                onChange={(event: React.SyntheticEvent, newView: DealGPTDiscoveryView) => {
                  analytics.event(
                    AnalyticsEvent.WM_IA_D_TAB_VIEW,
                    {
                      id: project.id,
                      value: newView,
                    },
                    {
                      projectId: project.id,
                      oldDiscoveryView: discoveryView,
                      newDiscoveryView: newView,
                    }
                  );
                  setDiscoveryView(newView);
                }}
                sx={{ borderBottom: '1px solid #C4C4C4', marginBottom: '16px' }}
              >
                <StyledTab value={DealGPTDiscoveryView.ALL_QUESTIONS} label="All Questions" />
                <StyledTab value={DealGPTDiscoveryView.SITE_VISIT_PREP} label="Site Visit Prep" />
                <StyledTab
                  value={DealGPTDiscoveryView.KEY_QUESTION_SUMMARY}
                  label="Key Question Summary"
                />
              </StyledTabs>

              {discoveryView === DealGPTDiscoveryView.KEY_QUESTION_SUMMARY ? (
                selectedRun && (
                  <KeyQuestionsSummary
                    projectId={project.id}
                    runId={selectedRun.id}
                    runScopes={runScopes}
                  />
                )
              ) : (
                <>
                  <StyledLoadingButton
                    variant="outlined"
                    loading={isLoadingReport}
                    loadingPosition="end"
                    endIcon={<></>}
                    onClick={() => handleGenerateReport()}
                  >
                    Generate Report
                  </StyledLoadingButton>
                  <div
                    id="results-and-statuses"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ width: '20%', marginTop: '10px', marginBottom: '24px' }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: '300', fontStyle: 'italic' }}>
                        Results:
                      </Typography>
                      <div style={{ height: '16px' }}>
                        <StatusProgressBar
                          quantity={aggregateProgressData.quantity}
                          quantityAnswered={aggregateProgressData.quantityAnswered}
                          quantityMoreInfoNeeded={aggregateProgressData.quantityMoreInfoNeeded}
                          quantityNotFound={aggregateProgressData.quantityNotFound}
                          quantitySkipped={aggregateProgressData.quantitySkipped}
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '10px',
                        margin: '10px 0px',
                        width: 'fit-content',
                      }}
                    >
                      {statuses.includes(DealGPTQuestionStatus.ANSWERED) && (
                        <IconButton
                          disableRipple
                          disabled={discoveryView !== DealGPTDiscoveryView.ALL_QUESTIONS}
                          onClick={() => toggleStatusFilters(DealGPTQuestionStatus.ANSWERED)}
                          sx={{
                            borderRadius: '20%',
                            height: '32px',
                            width: '32px',
                            opacity: discoveryView !== DealGPTDiscoveryView.ALL_QUESTIONS ? 0.5 : 1,
                            ...(isStatusSelected(DealGPTQuestionStatus.ANSWERED)
                              ? { bgcolor: '#CFEAD2' }
                              : { bgcolor: '#E8EEF8' }),
                            '&:hover': {
                              ...(isStatusSelected(DealGPTQuestionStatus.ANSWERED)
                                ? { bgcolor: '#CFEAD2' }
                                : { bgcolor: '#E8EEF8' }),
                            },
                          }}
                        >
                          <CheckCircleOutlined
                            sx={{ height: '16px', width: '16px', color: '#1dd566' }}
                          />
                        </IconButton>
                      )}
                      {/* The use of !important is to override the diabled behavior */}
                      {statuses.includes(DealGPTQuestionStatus.MORE_INFO_NEEDED) && (
                        <IconButton
                          disableRipple
                          disabled={discoveryView !== DealGPTDiscoveryView.ALL_QUESTIONS}
                          onClick={() =>
                            toggleStatusFilters(DealGPTQuestionStatus.MORE_INFO_NEEDED)
                          }
                          sx={{
                            borderRadius: '20%',
                            height: '32px',
                            width: '32px',
                            opacity: discoveryView !== DealGPTDiscoveryView.ALL_QUESTIONS ? 0.5 : 1,
                            ...(isStatusSelected(DealGPTQuestionStatus.MORE_INFO_NEEDED) ||
                            discoveryView === DealGPTDiscoveryView.SITE_VISIT_PREP
                              ? { bgcolor: '#FFE9CA !important' }
                              : { bgcolor: '#E8EEF8' }),
                            '&:hover': {
                              ...(isStatusSelected(DealGPTQuestionStatus.MORE_INFO_NEEDED)
                                ? { bgcolor: '#FFE9CA' }
                                : { bgcolor: '#E8EEF8' }),
                            },
                          }}
                        >
                          <ReportProblemOutlined
                            sx={{
                              color: '#ffc700',
                              height: '16px',
                              width: '16px',
                            }}
                          />
                        </IconButton>
                      )}
                      {statuses.includes(DealGPTQuestionStatus.NOT_FOUND) && (
                        <IconButton
                          disableRipple
                          disabled={discoveryView !== DealGPTDiscoveryView.ALL_QUESTIONS}
                          onClick={() => toggleStatusFilters(DealGPTQuestionStatus.NOT_FOUND)}
                          sx={{
                            borderRadius: '20%',
                            height: '32px',
                            width: '32px',
                            opacity: discoveryView !== DealGPTDiscoveryView.ALL_QUESTIONS ? 0.5 : 1,
                            ...(isStatusSelected(DealGPTQuestionStatus.NOT_FOUND) ||
                            discoveryView === DealGPTDiscoveryView.SITE_VISIT_PREP
                              ? { bgcolor: '#FFD4CA !important' }
                              : { bgcolor: '#E8EEF8' }),
                            '&:hover': {
                              ...(isStatusSelected(DealGPTQuestionStatus.NOT_FOUND)
                                ? { bgcolor: '#FFD4CA' }
                                : { bgcolor: '#E8EEF8' }),
                            },
                          }}
                        >
                          <NotInterestedOutlined
                            sx={{ color: '#f52c00', height: '16px', width: '16px' }}
                          />
                        </IconButton>
                      )}
                    </div>
                  </div>
                  <div id="search">
                    <FilterContainer maxWidth="lg" sx={{ marginBottom: '24px' }}>
                      <DebouncedSearchInput
                        value={searchQuery}
                        valueUpdated={(value) => {
                          analytics.event(
                            AnalyticsEvent.WM_IA_D_SEARCH_ENTERED,
                            {
                              id: project.id,
                              value: value,
                            },
                            {
                              projectId: project.id,
                              searchQuery: value,
                            }
                          );
                          setSearchQuery(value);
                        }}
                        onFocus={() => setDiscoverySearchSelected(true)}
                        onBlur={() => setDiscoverySearchSelected(false)}
                        shouldFocus={discoverySearchSelected}
                      />
                    </FilterContainer>
                  </div>
                  {runProgressData.filter((category) => category.quantity !== 0).length === 0 ? (
                    <Typography sx={{ marginBottom: '100px' }}>
                      No results found. Please adjust your filters or search query.
                    </Typography>
                  ) : (
                    <ScopesContainer className="categories-root" style={{ position: 'relative' }}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#1979CD',
                          cursor: 'pointer',
                          marginBottom: '16px',
                          alignSelf: 'flex-end',
                        }}
                        onClick={toggleCollapseExpandAllCategories}
                      >
                        {expandAllCategories ? 'Collapse all categories' : 'Expand all categories'}
                      </Typography>
                      {Object.entries(scopesProgress).map(([scope, progress]) => (
                        <ScopeAccordion
                          key={scope}
                          project={project}
                          progress={progress}
                          runId={selectedRun?.id ?? ''}
                          expandAll={expandAllCategories}
                          discoveryView={discoveryView}
                          selectedStatusFilters={selectedStatusFilters}
                          searchQuery={searchQuery}
                          isIndexing={isIndexing}
                          setShouldFetchRunProgress={setShouldFetchRunProgress}
                        />
                      ))}
                    </ScopesContainer>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscoveryView;
